import React, { SyntheticEvent, useState } from "react";
import Select, { SelectProps } from "@mui/material/Select";
import { MenuProps } from "@mui/material/Menu";

/** Cap a long list (departments) so it scrolls instead of growing past the screen. */
const MENU_MAX_HEIGHT = 360;
/** Open upward only when the space under the field cannot hold a useful list. */
const OPEN_UP_BELOW = 220;
const VIEWPORT_GAP = 8;

/**
 * Which side of the field the menu should sit on, and how tall it may grow.
 * A list taller than the viewport used to be slid upward by MUI until it fit,
 * which pulled it over the field that opened it (Bug 82084).
 */
export function pifSelectPlacement(spaceAbove: number, spaceBelow: number): { openUp: boolean; maxHeight: number } {
  const openUp = spaceBelow < OPEN_UP_BELOW && spaceAbove > spaceBelow;
  const room = Math.max(0, openUp ? spaceAbove : spaceBelow);
  return {
    openUp,
    maxHeight: Math.max(48, Math.min(MENU_MAX_HEIGHT, room || 48)),
  };
}

export function pifSelectMenuProps(spaceAbove: number, spaceBelow: number): Partial<MenuProps> {
  const { openUp, maxHeight } = pifSelectPlacement(spaceAbove, spaceBelow);
  return {
    anchorOrigin: { vertical: openUp ? "top" : "bottom", horizontal: "left" },
    transformOrigin: { vertical: openUp ? "bottom" : "top", horizontal: "left" },
    // Popover's default shift keeps 16px of viewport margin by sliding the menu,
    // which is what covers the field. Height is already capped to the free side.
    marginThreshold: null,
    slotProps: { paper: { style: { maxHeight } } },
  };
}

function usePifSelectMenu() {
  const [menuProps, setMenuProps] = useState<Partial<MenuProps>>(() => pifSelectMenuProps(0, MENU_MAX_HEIGHT));

  const onOpen = (event: SyntheticEvent<Element>) => {
    const target = event.currentTarget as HTMLElement;
    // MUI anchors the menu to the outlined input, the display node's parent.
    const field = target.parentElement ?? target;
    const rect = field.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP;
    const spaceAbove = rect.top - VIEWPORT_GAP;
    setMenuProps(pifSelectMenuProps(spaceAbove, spaceBelow));
  };

  return { MenuProps: menuProps, onOpen };
}

/** Select that opens from the field's left edge, above or below it, never on top of it. */
export function PifMenuSelect<T = unknown>(props: SelectProps<T>) {
  const menu = usePifSelectMenu();
  const handleOpen = (event: SyntheticEvent<Element>) => {
    menu.onOpen(event);
    props.onOpen?.(event);
  };
  return <Select<T> {...props} MenuProps={menu.MenuProps} onOpen={handleOpen} />;
}
